<template>
  <div :class="[
    $style.dialogStoresContainer,
    [{ [$style.dialogStoresContainerMobile]: isMobilePlatform }]
  ]">
    <div :class="$style.dialogStores">
      <div :class="$style.homeLogo">
        <img src="@/assets/img/LOGO.png"
             alt="Burger Boss">
      </div>

      <div v-if="!isMobilePlatform"
           :class="$style.dialogStoresTitle">
        Select a location
      </div>

      <v-autocomplete
        :class="$style.citiesSearch"
        :items="restaurants"
        :search-input.sync="searchParam"
        append-icon=""
        hide-no-data
        hide-details
        label="ZIP or City"
        color="#38AA3F"
        item-text="city"
        @input="changeSearch"
      />

      <ul :class="$style.storesList">
        <li
          v-for="store in filteredRests"
          :key="store.id"
          :class="$style.storesListItem">
          <div :class="$style.storeInfo">
            <h6>{{ store.city }}</h6>
            <p>{{ store.address }} </p>
            <p>{{ formatPhone(store.phone) }}</p>
          </div>
          <div :class="$style.storeButtons">
            <span>{{ formatDistance(store.distance) }}</span>
            <BaseButton :class="$style.storeLink"
                        :disabled="loading"
                        button-style="Main"
                        @click="selectStore(store.id)">
              Pick
            </BaseButton>
          </div>
        </li>
      </ul>
      <div v-if="restaurants.length === 0"
           :class="$style.loadingContainer">
        <LoadingSpinner/>
      </div>
      <div v-else-if="filteredRests.length === 0">
        <p :class="$style.noResults">
          No locations was found
        </p>
      </div>
      <div v-if="orderTypeError">
        <p :class="$style.error">
          No possible options for this restaurant
        </p>
      </div>

      <BaseButton v-if="!isMobilePlatform"
                  :class="$style.closeDialog"
                  button-style="Back">

        <router-link :class="$style.closeDialogLink"
                     to="/"
                     tag="button">
          <svg v-svg
               :class="$style.backIcon"
               symbol="straight-arrow"/>
          <span>Back</span>
        </router-link>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import { Action, Getter } from 'vuex-class';
import Vue from 'vue';
import BaseButton from '@/components/BaseButton';
import { formatPhoneDots } from '@/lib/helpers';
import { ORDER_SET_RESTAURANT, ORDER_SET_TYPE } from '@/apps/cart/store/types';
import { MENU_LOAD, LAZY_MENU_LOAD } from '@/apps/menu/store/types';
import { ORDER_TYPES } from '@/config/constants';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';

/**
 * Page to pick nearest restaurant.
 */
@Component({
  components: { BaseButton, LoadingSpinner },
  props:      {
    nextRouteName: {
      type:     String,
      required: false,
    },
  },
})
export default class RestaurantPicker extends Vue {
  searchParam = '';
  formatPhone = formatPhoneDots;
  @Getter restaurants;
  @Getter isMobilePlatform;
  @Getter doesDeviceSupportCurbsidePickup;
  @Getter restaurantId;
  @Action(MENU_LOAD) loadMenu;
  @Action(LAZY_MENU_LOAD) loadLazyMenu;
  @Action(ORDER_SET_RESTAURANT) setOrderRest;
  @Action(ORDER_SET_TYPE) setOrderType;

  loading = false;
  orderTypeError = null;

  /**
   * Filter restaurants list by city or by zip
   */
  get filteredRests() {
    if (this.searchParam) {
      const searchCityString = this.searchParam.toLowerCase();

      return this.restaurants.filter(item =>
        item.city.toLowerCase().includes(searchCityString)
        || item.zip.toLowerCase().includes(searchCityString));
    }

    return this.restaurants;
  }

  /**
   * Format distance of restaurant
   */
  formatDistance(distance) {
    if (distance !== null) {
      return `${distance} mi`;
    }

    return '';
  }

  /**
   * Set `searchParam` param from filter restaurants
   */
  changeSearch(city) {
    this.searchParam = city;
  }

  /**
   * Save selected rest to store and open menu
   * @param storeId
   */
  async selectStore(storeId) {
    this.orderTypeError = null;
    try {
      this.loading = true;
      await this.setOrderRest(storeId);
      this.getPossibleOptionsAndRedirect(storeId);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get possible order options for the restaurant
   * Redirect to options or menu if only one option is available
   * @param storeId
   */
  getPossibleOptionsAndRedirect(storeId) {
    const currentStore = this.restaurants
      .find(restaurant => restaurant.id === storeId);

    const possibleOptions = {
      curbside: currentStore.isCurbsideSupported && this.doesDeviceSupportCurbsidePickup,
      pickup:   currentStore.isPickupSupported,
    };

    if (this.nextRouteName) {
      if (possibleOptions.curbside && !possibleOptions.pickup) {
        this.setOrderType(ORDER_TYPES.curbside);
      } else if (possibleOptions.pickup && !possibleOptions.curbside) {
        this.setOrderType(ORDER_TYPES.pickup);
      }

      this.$router.push({ name: this.nextRouteName });

      return;
    }

    if (possibleOptions.curbside && possibleOptions.pickup) {
      this.$router.push({ name: 'options' });

      return;
    }

    if (possibleOptions.curbside) {
      this.setOrderType(ORDER_TYPES.curbside);
      this.$router.push({ name: 'menu' });

      return;
    }

    if (possibleOptions.pickup) {
      this.setOrderType(ORDER_TYPES.inStore);
      this.$router.push({ name: 'menu' });

      return;
    }

    this.orderTypeError = true;
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";
@import "~@/style/helpers.scss";

.dialogStoresContainer {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  /* Center and scale the image nicely */
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.dialogStores {
  background-color: $white;
  width: 824px;
  max-width: calc(100% - 40px); /* 100% - minimum margins */
  margin: 0 auto;
  padding: 40px;
  position: relative;

  .homeLogo {
    margin-bottom: 10px;
    text-align: center;
  }

  .dialogStoresTitle {
    height: 40px;
    font-weight: 700;
    text-align: center;
    background-color: $white;
    text-transform: uppercase;
    line-height: 40px;
    box-shadow: 0 1px 2px 0 rgba(181, 181, 181, 0.6);
    display: none;
  }
}

.loadingContainer {
  position: relative;
  height: 100%;
  min-height: 200px;
}

.storesList {
  margin-top: 20px;
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-left: -11px;
  margin-right: -11px;
  display: flex;
  flex-flow: row wrap;
}

.storesListItem {
  width: calc(50% - 22px); /* 100% - side margins */
  margin-top: 22px;
  margin-left: 11px;
  margin-right: 11px;
  border: 1px solid $gray-color;
  padding: 20px 17px 17px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Gotham', sans-serif;

  span {
    color: $black-semi-transparent;
    font-size: 12px;
    font-style: italic;
    text-align: center;
  }
}

.storeButtons {
  display: flex;
  flex-direction: column;

  .storeLink {
    border-radius: 17px;
    padding: 9px 12px;
    text-align: center;
    margin-top: 6px;

    div {
      font-size: 18px;
      letter-spacing: -0.43px;
      line-height: 16px;
    }
  }
}

.closeDialog {
  position: absolute;
  top: 30px;
  left: 30px;
  width: 164px;
  height: 79px;
  padding: 0;
  div {
    width: 162px;
    height: 77px;
  };
  .backIcon {
    fill: $black-slightly-transparent;
    width: 24px;
    height: 24px;
  }
  span {
    padding-left: 16px;
    text-transform: uppercase;
  }

  &:hover,
  &:focus,
  &:active {
    position: absolute;
  }
}

.closeDialogLink {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
}

.outIcon {
  width: 48.5px;
  height: 48.5px;
}

.curbsideIcon {
  width: 62px;
  height: 32px;
}

.homeMenu {
  z-index: 3;
  width: 100%;
  min-height: 100%;
}

.error  {
  margin-top: 10px;
  text-align: center;
  color: $red-color;
}

@media screen and (max-width: $mobile-breakpoint-max) {
  .dialogStoresContainer {
    // Background-image define inline. Not need in mobile.
    background-image: none !important;
  }

  .dialogStores .closeDialog {
    border: none;
    width: 16px;
    height: 16px;
    top: 9px;
    left: 16px;
    font-size: 0;

    div {
      width: 30px;
      height: 30px;
      position: relative;
      top: -10px;
      z-index: 50;
    };

    .backIcon,
    span {
      display: none;
    }

    &:hover,
    &:focus,
    &:active {
      background-color: $white;
    }

    &::before {
      content: '';
      position: absolute;
      top: 3px;
      left: 0;
      width: 16px;
      height: 2px;
      background-color: $black;
      transform: rotate(45deg);
      opacity: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 0;
      width: 16px;
      height: 2px;
      background-color: $black;
      transform: rotate(-45deg);
      opacity: 1;
    }
  }
  .storesList {
    flex-direction: column;
  }
  .storesListItem {
    width: calc(100% - 22px); /* 100% - side margins */
  }

  .dialogStores {
    padding: 0;
    background-color: $light-choice-bg;
    width: 100%;
    max-width: 100%;
    height: 100%;
    margin: 0;

    .homeLogo {
      display: none;
    }

    .dialogStoresTitle {
      display: block;
    }

    .citiesSearch {
      padding: 10px 20px 0;
    }
  }
  .storesListItem {
    background-color: $white;
    border: none;
    box-shadow: 0 1px 2px 0 rgba(181, 181, 181, 0.6);
    margin-top: 0;
    margin-bottom: 6px;

    h4 {
      margin-bottom: 6px;
    }
  }
}

/* Mobile app style */
.dialogStoresContainerMobile {
  .storesListItem,
  .storesList {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
}

.noResults {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/RestaurantPicker.vue